import { useMediaQuery } from '@mui/material';

export const AppBreakpoints = {
  xs: 480,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  laptopSm: 1366,
  wideScreen: 1500,
  '2xl': 1536,
  laptop: 1680,
} as const;

export type TBreakpointKey = keyof typeof AppBreakpoints;
export type TBreakpointValue = TBreakpointKey | number;

/**
 * Custom hook to check if current viewport width is less than or equal to a breakpoint (max-width)
 */
export const useBreakpointDown = (breakpoint: TBreakpointValue): boolean => {
  const width =
    typeof breakpoint === 'number' ? breakpoint : AppBreakpoints[breakpoint];
  return useMediaQuery(`(max-width: ${width}px)`);
};

/**
 * Custom hook to check if current viewport width is greater than or equal to a breakpoint (min-width)
 */
export const useBreakpointUp = (breakpoint: TBreakpointValue): boolean => {
  const width =
    typeof breakpoint === 'number' ? breakpoint : AppBreakpoints[breakpoint];
  return useMediaQuery(`(min-width: ${width}px)`);
};

/**
 * Custom hook to check if current viewport width is between min and max breakpoints
 */
export const useBreakpointBetween = (
  min: TBreakpointValue,
  max: TBreakpointValue
): boolean => {
  const minWidth = typeof min === 'number' ? min : AppBreakpoints[min];
  const maxWidth = typeof max === 'number' ? max : AppBreakpoints[max];
  return useMediaQuery(
    `(min-width: ${minWidth}px) and (max-width: ${maxWidth}px)`
  );
};

export const useGetAppBreakPoints = () => {
  const isMobile = useBreakpointDown('xs');
  const isPortraitTablet = useBreakpointDown('md');
  const isLandscapeTablet = useBreakpointDown('lg');
  const isLaptopSM = useBreakpointDown('laptopSm');
  const isLessThan1280 = useBreakpointDown('xl');
  const isLessThan1500 = useBreakpointDown('wideScreen');

  const isLaptop = useBreakpointUp('laptop');
  const isDesktopUp = useBreakpointUp('xl');

  return {
    isMobile,
    isPortraitTablet,
    isLandscapeTablet,
    isLaptopSM,
    isLessThan1280,
    isLessThan1500,
    isLaptop,
    isDesktopUp,
  };
};
